import { onAuthStateChanged } from "firebase/auth";
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import { USER_ROLES } from "../models/UserModel";
import UserService from "../services/UserService";

// Create context
const UserContext = createContext();
export { UserContext }; // Add named export for UserContext

// User provider component
export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to refresh user role from Firestore
  const refreshUserRole = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      // Force token refresh first
      await auth.currentUser.getIdToken(true);
      console.log("Auth token refreshed");

      // Then get updated user data
      const userData = await UserService.getUserById(currentUser.uid);

      if (userData) {
        setUserRole(userData.role);
        console.log("User role refreshed:", userData.role);
      }
    } catch (error) {
      console.error("Error refreshing user role:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          // Get user data from Firestore including role
          const userData = await UserService.getUserById(user.uid);

          if (userData) {
            setUserRole(userData.role);
          } else {
            // If user doesn't exist in Firestore yet, initialize with user role
            await UserService.initializeUserIfNeeded(user);
            setUserRole(USER_ROLES.USER);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
          setUserRole(USER_ROLES.USER); // Default to user role on error
        }
      } else {
        setUserRole(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Check if user is a manager
  const isManager = () => userRole === USER_ROLES.MANAGER;

  // For backwards compatibility
  const isTeacher = () => !isManager();

  return (
    <UserContext.Provider
      value={{ currentUser, userRole, loading, isManager, isTeacher, refreshUserRole }}
    >
      {children}
    </UserContext.Provider>
  );
}

// Custom hook to use the user context
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export default UserContext;
